/**
 * Iter XX.142 — Simplified landing router.
 * Iter XX.189 — Startup auth-aware routing.
 *
 * Every visitor lands on `InstantChatLanding` UNLESS an authenticated
 * session already exists — in which case they go straight to `/chat`,
 * their normal authenticated Gully experience. This fixes the Android
 * cold-start flash where a returning signed-in user briefly saw the
 * guest landing before any route decided.
 *
 * Behaviour guarantees (both web and Capacitor native):
 *   · Anonymous / new user  → `InstantChatLanding` (unchanged).
 *   · Authenticated user    → `<Navigate to="/chat" replace />`.
 *   · Auth still resolving  → brief loading placeholder (same style as
 *                             the `Protected` wrapper in App.js), ONLY
 *                             when a token actually exists — new users
 *                             with no token never see it.
 *
 * The legacy A/B variants (Landing, LandingV2, LandingLoop,
 * R3LiteOnboarding) have been retired — the founder rule is one
 * architecture, different messaging. Campaign-specific copy already
 * lives in `data/landingPages.js` and is resolved by
 * `InstantChatLanding` itself.
 *
 * A single kill-switch flag remains for emergency rollback. When
 * `activation_landing_kill_switch=true` we render a minimal
 * apology screen — never the old marketing variants — because the
 * conversation-first flow is now the only supported entry.
 */
import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import InstantChatLanding from "./InstantChatLanding";
import { setLandingVariant } from "../lib/landingAnalytics";
import { useFlag } from "../lib/featureFlags";
import { useAuth } from "../lib/auth";
import { hasToken } from "../lib/nativeTokenStorage";

export default function LandingSwitch() {
  const killSwitch = useFlag("activation_landing_kill_switch");
  const { user, loading } = useAuth();

  useEffect(() => {
    // Iter XX.142 — Single canonical variant tag. Retained for
    // funnel analysis so old Clarity reports keep resolving.
    setLandingVariant("instantchat_v1");
  }, []);

  if (killSwitch) {
    return (
      <div
        data-testid="activation-landing-kill-switch"
        className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-100 p-6 text-center"
      >
        <div className="max-w-md">
          <div className="text-lg mb-2">gully</div>
          <p className="text-sm text-neutral-400">
            We're briefly refreshing the entry experience. Please try again
            in a moment.
          </p>
        </div>
      </div>
    );
  }

  // Iter XX.189 — Startup auth-aware routing.
  //
  // On native (Capacitor iOS/Android) the token is hydrated from the
  // OS secure store during `initTokenStorage()` at boot, BEFORE React
  // renders (see index.js). So `hasToken()` is a synchronous, correct
  // signal here on both native and web.
  //
  // If the user is confirmed → redirect to /chat.
  // If the auth check is still pending AND a token exists → show a
  //   brief splash so we don't flash the guest landing.
  // If no token exists at all → skip the splash (new users must see
  //   the landing immediately; that's the guest-first invariant).
  if (user) {
    return <Navigate to="/chat" replace />;
  }
  if (loading && hasToken()) {
    return (
      <div
        data-testid="landing-auth-restore"
        className="min-h-screen flex items-center justify-center bg-neutral-950 text-neutral-500"
      >
        …
      </div>
    );
  }

  return <InstantChatLanding />;
}
